import { XClose } from "@untitledui/icons";
import { useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { useAddMerchantsToWorkspace } from "@/api/admin/workspace-mutations";
import { Button } from "@/components/base/buttons/button";
import { error } from "@/providers/ToastProvider";
import { WorkspaceBrandsPicker } from "../components/workspace-brands-picker";

type Props = {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after brands are successfully added (or skipped). Defaults to closing the modal. */
  onDone?: () => void;
  /** IDs of merchants already in this workspace — hidden from the picker */
  excludeMerchantIds?: string[];
};

export const SelectBrandsForWorkspaceModal = ({
  workspaceId,
  open,
  onOpenChange,
  onDone,
  excludeMerchantIds = [],
}: Props) => {
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const addMerchants = useAddMerchantsToWorkspace();

  const handleAdd = async () => {
    try {
      if (selectedBrandIds.length > 0) {
        await addMerchants.mutateAsync({
          workspaceId,
          merchantIds: selectedBrandIds,
        });
      }
    } catch {
      error("Failed to add merchants to workspace");
      return;
    }
    setSelectedBrandIds([]);
    onOpenChange(false);
    onDone?.();
  };

  const handleCancel = () => {
    setSelectedBrandIds([]);
    onOpenChange(false);
    onDone?.();
  };

  return (
    <AriaModalOverlay
      isOpen={open}
      onOpenChange={onOpenChange}
      isDismissable={false}
      className="fixed inset-0 z-[120] bg-black/70"
    >
      <AriaModal className="dark-mode absolute inset-x-8 inset-y-8 outline-none bg-primary rounded-2xl overflow-hidden flex flex-col">
        <AriaDialog className="w-full h-full flex flex-col outline-none py-8">
          <div className="shrink-0 flex justify-end px-8 pb-2">
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-secondary border border-primary rounded-lg hover:bg-white/10 transition-colors"
            >
              <XClose className="size-4" />
              Cancel
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center px-8 overflow-hidden min-h-0">
            <div className="w-full max-w-[560px] flex flex-col flex-1 overflow-hidden gap-3">
              <WorkspaceBrandsPicker
                value={selectedBrandIds}
                onChange={setSelectedBrandIds}
                excludeMerchantIds={excludeMerchantIds}
              />
            </div>
          </div>

          <div className="shrink-0 flex justify-center px-8 py-6">
            <div className="w-full max-w-[560px]">
              <Button
                size="lg"
                color="primary"
                className="w-full"
                onClick={handleAdd}
                isDisabled={addMerchants.isPending}
              >
                {addMerchants.isPending ? "Adding…" : "Add merchant to Workspace"}
              </Button>
            </div>
          </div>
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
};
